import Alert, { AlertOptions } from 'react-s-alert';
import { Params, Query } from 'redux-little-router';

import { PerlRestApiResult } from '../api/RestApiManager';
import { NoticeIcon } from '../components/Notice/Notice';
import { Shape2Icon } from '../components/Shape2/Shape2';
import {
    ERROR_PROPORTION_CHECKPOINT,
    SUCCESS_PROPORTION_CHECKPOINT
} from '../service/NoticeTypeHelper/NoticeTypeHelper';
import { BannerId } from '../typings/banners';
import {
    CellKeyFields,
    ClientInfo,
    ColumnsSettingsViewDict,
    CommonSettingEditorDiff,
    LinkTarget,
    SaveSuccessData
} from '../typings/common';
import { DocCenterData, DocCenterTooltip } from '../typings/doc-center';
import {
    CampaignId,
    CampaignsCalcTypeFilter,
    CampaignType,
    CopyTooltipStatus,
    FilterGridViewDict,
    RejectReasonsData,
    ScreenAdditionalFilter
} from '../typings/domain';
import { PeriodFilterValue } from '../typings/filters';
import { GoalDecl } from '../typings/goals';
import { GdRecommendationStatus } from '../typings/graphql-grid';
import {
    GridAction,
    GridActionFeatures,
    GridData,
    GridEditCellStatus,
    GridEditedEntityAddData,
    GridEditedEntityType,
    GridSelection,
    GridSourceParams,
    GridTotalStatsInfo,
    GridViewType
} from '../typings/grid';
import { DuplicateItem, GroupId } from '../typings/groups';
import { PhrasesFeaturesData } from '../typings/inspector';
import {
    RecommendationKey,
    RecommendationType
} from '../typings/recommendations';
import {
    BannerAimingPopupData,
    MinusPhraseSelectorItemsData,
    PhraseAdjustmentDataChange
} from '../typings/state';

export const ON_GRID_SOURCE_PARAMS_CHANGED = 'ON_GRID_SOURCE_PARAMS_CHANGED';
export type ON_GRID_SOURCE_PARAMS_CHANGED = typeof ON_GRID_SOURCE_PARAMS_CHANGED;
export const UPDATE_GRID_DATA = 'UPDATE_GRID_DATA';
export type UPDATE_GRID_DATA = typeof UPDATE_GRID_DATA;
export const UPDATE_GRID_SELECTION = 'UPDATE_GRID_SELECTION';
export type UPDATE_GRID_SELECTION = typeof UPDATE_GRID_SELECTION;
export const UPDATE_SUGGEST_DATA = 'UPDATE_SUGGEST_DATA';
export type UPDATE_GRID_FEATURES = typeof UPDATE_GRID_FEATURES;
export const UPDATE_GRID_FEATURES = 'UPDATE_GRID_FEATURES';
export type UPDATE_SUGGEST_DATA = typeof UPDATE_SUGGEST_DATA;
export const RESET_SUGGEST_DATA = 'RESET_SUGGEST_DATA';
export type RESET_SUGGEST_DATA = typeof RESET_SUGGEST_DATA;
export const UPDATE_ACTION_PROGRESS = 'UPDATE_ACTION_PROGRESS';
export type UPDATE_ACTION_PROGRESS = typeof UPDATE_ACTION_PROGRESS;
export const OPEN_EDIT_CAMPAIGN_POPUP = 'OPEN_EDIT_CAMPAIGN_POPUP';
export type OPEN_EDIT_CAMPAIGN_POPUP = typeof OPEN_EDIT_CAMPAIGN_POPUP;
export const OPEN_EDIT_GROUPS_POPUP = 'OPEN_EDIT_GROUPS_POPUP';
export type OPEN_EDIT_GROUPS_POPUP = typeof OPEN_EDIT_GROUPS_POPUP;
export const CLOSE_EDIT_POPUP = 'CLOSE_EDIT_POPUP';
export type CLOSE_EDIT_POPUP = typeof CLOSE_EDIT_POPUP;
export const CHANGE_COLUMNS_GRID_VISIBILITY = 'CHANGE_COLUMNS_GRID_VISIBILITY';
export type CHANGE_COLUMNS_GRID_VISIBILITY = typeof CHANGE_COLUMNS_GRID_VISIBILITY;
export const CHANGE_COLUMNS_GRID_ORDER = 'CHANGE_COLUMNS_GRID_ORDER';
export type CHANGE_COLUMNS_GRID_ORDER = typeof CHANGE_COLUMNS_GRID_ORDER;
export type UPDATE_GRID_COLUMNS_WIDTH = 'UPDATE_GRID_COLUMNS_WIDTH';
export const UPDATE_GRID_COLUMNS_WIDTH = 'UPDATE_GRID_COLUMNS_WIDTH';
export const UPDATE_SCROLL_POSITION = 'UPDATE_SCROLL_POSITION';
export type UPDATE_SCROLL_POSITION = typeof UPDATE_SCROLL_POSITION;
export const UPDATE_CELL_EDIT_DATA = 'UPDATE_CELL_EDIT_DATA';
export type UPDATE_CELL_EDIT_DATA = typeof UPDATE_CELL_EDIT_DATA;
export const DELETE_CELL_EDIT_DATA = 'DELETE_CELL_EDIT_DATA';
export type DELETE_CELL_EDIT_DATA = typeof DELETE_CELL_EDIT_DATA;
export const UPDATE_CONTEXT_IDS = 'UPDATE_CONTEXT_IDS';
export type UPDATE_CONTEXT_IDS = typeof UPDATE_CONTEXT_IDS;
export const SCROLL_TABLE_START = 'SCROLL_TABLE_START';
export type SCROLL_TABLE_START = typeof SCROLL_TABLE_START;
export const SCROLL_TABLE_END = 'SCROLL_TABLE_END';
export type SCROLL_TABLE_END = typeof SCROLL_TABLE_END;
export const UPDATE_ADDITIONAL_FILTER = 'UPDATE_ADDITIONAL_FILTER';
export type UPDATE_ADDITIONAL_FILTER = typeof UPDATE_ADDITIONAL_FILTER;
export const SWITCH_GRID_TOTAL_BOX_VISIBLE = 'SWITCH_GRID_TOTAL_BOX_VISIBLE';
export type SWITCH_GRID_TOTAL_BOX_VISIBLE = typeof SWITCH_GRID_TOTAL_BOX_VISIBLE;
export const UPDATE_GRID_FILTER_DICT = 'UPDATE_GRID_FILTER_DICT';
export type UPDATE_GRID_FILTER_DICT = typeof UPDATE_GRID_FILTER_DICT;
export const UPDATE_GRID_FILTER_PERIOD = 'UPDATE_GRID_FILTER_PERIOD';
export type UPDATE_GRID_FILTER_PERIOD = typeof UPDATE_GRID_FILTER_PERIOD;
export const UPDATE_CALC_TYPE_FILTER = 'UPDATE_CALC_TYPE_FILTER';
export type UPDATE_CALC_TYPE_FILTER = typeof UPDATE_CALC_TYPE_FILTER;

export interface UpdateColumnsWidthData {
    type: UPDATE_GRID_COLUMNS_WIDTH;
    columns: ColumnsSettingsViewDict;
}

export function gridScreenUpdateColumnsWidth(
    columns: ColumnsSettingsViewDict
): UpdateColumnsWidthData {
    return {
        type: UPDATE_GRID_COLUMNS_WIDTH,
        columns
    };
}

export interface ColumnsVisibilityChangeData {
    type: CHANGE_COLUMNS_GRID_VISIBILITY;
    columns: ColumnsSettingsViewDict;
}

export function gridScreenChangeColumnsVisibility(
    columns: ColumnsSettingsViewDict
): ColumnsVisibilityChangeData {
    return {
        type: CHANGE_COLUMNS_GRID_VISIBILITY,
        columns
    };
}

export interface ColumnsOrderChangeData {
    type: CHANGE_COLUMNS_GRID_ORDER;
    view: string;
    columnOrder: Array<string>;
}

export function gridScreenChangeColumnsOrder(
    view: string,
    columnOrder: Array<string>
): ColumnsOrderChangeData {
    return {
        type: CHANGE_COLUMNS_GRID_ORDER,
        view,
        columnOrder
    };
}

export interface UpdateGridData {
    type: UPDATE_GRID_DATA;
    gridData: GridData;
    clientInfo: ClientInfo;
    scrollPosition?: number;
    totalStats?: GridTotalStatsInfo;
    hasObjectsOverLimit: boolean;
    gridReqId: string;
    goalsSettings?: GoalDecl[];
    phraseContextData?: PhrasesFeaturesData;
}

export interface ScrollTableStart {
    type: SCROLL_TABLE_START;
}

export interface ScrollTableEnd {
    type: SCROLL_TABLE_END;
}

export interface UpdateGridSelection {
    type: UPDATE_GRID_SELECTION;
    selection: GridSelection;
}

export interface UpdateSuggestData {
    type: UPDATE_SUGGEST_DATA;
    textSuggestItems: Array<string>;
}

export interface ResetSuggestData {
    type: RESET_SUGGEST_DATA;
}

export interface UpdateFeatures {
    type: UPDATE_GRID_FEATURES;
    gridActionFeatures: GridActionFeatures;
}

export interface UpdateActionProgress {
    type: UPDATE_ACTION_PROGRESS;
    isActionInProgress: boolean;
}

export interface EditCampaign {
    type: OPEN_EDIT_CAMPAIGN_POPUP;
    campaignId?: string;
    campaignType?: CampaignType;
    login: string;
}

export interface EditGroups {
    type: OPEN_EDIT_GROUPS_POPUP;
    groupIds?: string[];
    bannerIds?: string[];
    campaignId: string;
    campaignType: CampaignType;
    login: string;
    anchor?: string;
}

export interface UpdateScrollPosition {
    scrollPosition?: number;
    type: UPDATE_SCROLL_POSITION;
}

export interface UpdateEditCellData {
    type: UPDATE_CELL_EDIT_DATA;
    cellKeysFields: CellKeyFields;
    data: GridEditCellStatus;
}

export interface DeteleEditCellData {
    type: DELETE_CELL_EDIT_DATA;
    cellKeysFields: CellKeyFields;
}

export interface CloseEditPopup {
    type: CLOSE_EDIT_POPUP;
}

export enum NoticeType {
    error = 'error',
    warning = 'warning',
    success = 'success',
    info = 'info'
}

export function updateScrollPosition(position?: number): UpdateScrollPosition {
    return {
        type: UPDATE_SCROLL_POSITION,
        scrollPosition: position
    };
}

export const GRID_SCREEN_CANCEL_CELL_EDIT = 'GRID_SCREEN_CANCEL_CELL_EDIT';
export type GRID_SCREEN_CANCEL_CELL_EDIT = typeof GRID_SCREEN_CANCEL_CELL_EDIT;

export interface GridScreenCancelCellEditAction {
    type: GRID_SCREEN_CANCEL_CELL_EDIT;
    id: string;
    columnName: string;
}

export function cancelCellEdit(
    id: string,
    columnName: string
): GridScreenCancelCellEditAction {
    return {
        type: GRID_SCREEN_CANCEL_CELL_EDIT,
        id,
        columnName
    };
}

/* Переход в мастер отчетов */
export const GRID_SCREEN_GO_TO_STATISTICS = 'GRID_SCREEN_GO_TO_STATISTICS';
export type GRID_SCREEN_GO_TO_STATISTICS = typeof GRID_SCREEN_GO_TO_STATISTICS;

export interface GridScreenGoToStatisticsAction {
    type: GRID_SCREEN_GO_TO_STATISTICS;
    target: LinkTarget;
    campaignIds?: string[];
}

export function goToStatistics(
    target: LinkTarget = LinkTarget.blank,
    campaignIds?: string[]
): GridScreenGoToStatisticsAction {
    return {
        type: GRID_SCREEN_GO_TO_STATISTICS,
        target,
        campaignIds
    };
}

/* Переход в оферту подтверждения модерации */
export const GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER =
    'GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER';
export type GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER = typeof GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER;

export interface GridScreenGoToCampaignModerationOfferAction {
    type: GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER;
    target: LinkTarget;
    campaignId?: string;
}

export function goToCampaignModerationOffer(
    target: LinkTarget = LinkTarget.self,
    campaignId?: string
): GridScreenGoToCampaignModerationOfferAction {
    return {
        type: GRID_SCREEN_GO_TO_CAMPAIGN_MODERATION_OFFER,
        target,
        campaignId
    };
}

export const GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS =
    'GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS';
export type GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS = typeof GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS;
export interface GridScreenGoToDirectPaymentInstructions {
    type: GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS;
}
export function goToDirectPaymentInstructions(): GridScreenGoToDirectPaymentInstructions {
    return {
        type: GRID_SCREEN_GO_TO_DIRECT_PAYMENT_INSTRUCTIONS
    };
}

export const GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT =
    'GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT';
export type GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT = typeof GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT;

export interface GridScreenGoToBannersMassEditAction {
    type: GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT;
}

export function goToBannersMassEdit(): GridScreenGoToBannersMassEditAction {
    return {
        type: GRID_SCREEN_GO_TO_BANNERS_MASS_EDIT
    };
}

export const GRID_SCREEN_GO_TO_BANNERS_EDIT = 'GRID_SCREEN_GO_TO_BANNERS_EDIT';
export type GRID_SCREEN_GO_TO_BANNERS_EDIT = typeof GRID_SCREEN_GO_TO_BANNERS_EDIT;

export interface GridScreenGoToBannersEditAction {
    type: GRID_SCREEN_GO_TO_BANNERS_EDIT;
    login: string;
    campaignsIds: string[];
    bannersIds: BannerId[];
}

export function goToBannersEdit(
    login: string,
    campaignsIds: string[],
    bannersIds: BannerId[]
): GridScreenGoToBannersEditAction {
    return {
        type: GRID_SCREEN_GO_TO_BANNERS_EDIT,
        login,
        campaignsIds,
        bannersIds
    };
}

export const GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT =
    'GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT';
export type GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT = typeof GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT;

export interface GoToInternalBannersEditAction {
    type: GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT;
    login: string;
    campaignsIds: string[];
    bannersIds: BannerId[];
}

export function goToInternalBannersEdit(
    login: string,
    campaignsIds: string[],
    bannersIds: BannerId[]
): GoToInternalBannersEditAction {
    return {
        type: GRID_SCREEN_GO_TO_INTERNAL_BANNERS_EDIT,
        login,
        campaignsIds,
        bannersIds
    };
}

export const GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT =
    'GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT';
export type GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT = typeof GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT;

export interface GridScreenGoToInternalBannersMassEditAction {
    type: GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT;
}

export function goToInternalBannersMassEdit(): GridScreenGoToInternalBannersMassEditAction {
    return {
        type: GRID_SCREEN_GO_TO_INTERNAL_BANNERS_MASS_EDIT
    };
}

export const GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION =
    'GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION';
export type GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION = typeof GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION;

export interface GoToInternalBannersEditForCreationAction {
    type: GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION;
    login: string;
    campaignId: string;
    groupId: string;
}

export function goToInternalBannersEditForCreation(
    login: string,
    campaignId: string,
    groupId: string
): GoToInternalBannersEditForCreationAction {
    return {
        type: GO_TO_INTERNAL_BANNERS_EDIT_FOR_CREATION,
        login,
        campaignId,
        groupId
    };
}

export const GRID_SCREEN_GO_TO_GROUPS_EDIT = 'GRID_SCREEN_GO_TO_GROUPS_EDIT';
export type GRID_SCREEN_GO_TO_GROUPS_EDIT = typeof GRID_SCREEN_GO_TO_GROUPS_EDIT;

export interface GridScreenGoToGroupsEditAction {
    type: GRID_SCREEN_GO_TO_GROUPS_EDIT;
    login: string;
    campaignsIds: string[];
    groupsIds: string[];
    isNewEmptyGroup: boolean;
    replaceLocation?: boolean;
}

export function goToGroupsEdit(
    login: string,
    campaignsIds: string[],
    groupsIds: string[],
    isNewEmptyGroup?: boolean,
    replaceLocation?: boolean
): GridScreenGoToGroupsEditAction {
    return {
        type: GRID_SCREEN_GO_TO_GROUPS_EDIT,
        login,
        campaignsIds,
        groupsIds,
        isNewEmptyGroup: Boolean(isNewEmptyGroup),
        replaceLocation
    };
}

export const GO_TO_GROUPS_EDIT_FOR_CREATION = 'GO_TO_GROUPS_EDIT_FOR_CREATION';
export type GO_TO_GROUPS_EDIT_FOR_CREATION = typeof GO_TO_GROUPS_EDIT_FOR_CREATION;

export interface GoToGroupsEditForCreationAction {
    type: GO_TO_GROUPS_EDIT_FOR_CREATION;
    login: string;
    campaignId: CampaignId;
}

export function goToGroupsEditForCreation(
    login: string,
    campaignId: string
): GoToGroupsEditForCreationAction {
    return {
        type: GO_TO_GROUPS_EDIT_FOR_CREATION,
        login,
        campaignId
    };
}

export const GO_TO_BANNERS_EDIT_FOR_CREATION =
    'GO_TO_BANNERS_EDIT_FOR_CREATION';
export type GO_TO_BANNERS_EDIT_FOR_CREATION = typeof GO_TO_BANNERS_EDIT_FOR_CREATION;

export interface GoToBannersEditForCreationAction {
    type: GO_TO_BANNERS_EDIT_FOR_CREATION;
    login: string;
    campaignId: CampaignId;
    groupId: GroupId;
}

export function goToBannersEditForCreation(
    login: string,
    campaignId: CampaignId,
    groupId: GroupId
): GoToBannersEditForCreationAction {
    return {
        type: GO_TO_BANNERS_EDIT_FOR_CREATION,
        login,
        campaignId,
        groupId
    };
}

export const GRID_SCREEN_GO_TO_LOGS = 'GRID_SCREEN_GO_TO_LOGS';
export type GRID_SCREEN_GO_TO_LOGS = typeof GRID_SCREEN_GO_TO_LOGS;

export interface GridScreenGoToLogsAction {
    type: GRID_SCREEN_GO_TO_LOGS;
}

export function goToLogs(): GridScreenGoToLogsAction {
    return {
        type: GRID_SCREEN_GO_TO_LOGS
    };
}

/* Выводит нотификационное сообщение */
export function showNoticeMessage(
    type: NoticeType,
    message: string | JSX.Element,
    options: AlertOptions = {}
): void {
    let icon: NoticeIcon;

    switch (type) {
        case NoticeType.success:
            icon = { name: Shape2Icon.tick, size: 18 };
            break;

        case NoticeType.error:
        default:
            icon = { name: Shape2Icon.warning, size: 18 };
            break;
    }

    Alert[type](message as string, {
        ...options,
        customFields: {
            ...options.customFields,
            icon: icon
        }
    });
}

/* Обработчик события совершения массового действия над элементами таблицы */
export const GRID_SCREEN_HANDLE_BULK_ITEMS = 'GRID_SCREEN_HANDLE_BULK_ITEMS';
export type GRID_SCREEN_HANDLE_BULK_ITEMS = typeof GRID_SCREEN_HANDLE_BULK_ITEMS;

export interface GridScreenHandleBulkItemsAction {
    type: GRID_SCREEN_HANDLE_BULK_ITEMS;
    bulkAction: GridAction;
}

export function handleGridItemsAction(
    bulkAction: GridAction
): GridScreenHandleBulkItemsAction {
    return {
        type: GRID_SCREEN_HANDLE_BULK_ITEMS,
        bulkAction
    };
}

export const GRID_SCREEN_HANDLE_ITEM_ACTION = 'GRID_SCREEN_HANDLE_ITEM_ACTION';
export type GRID_SCREEN_HANDLE_ITEM_ACTION = typeof GRID_SCREEN_HANDLE_ITEM_ACTION;

export interface GridScreenHandleItemAction {
    type: GRID_SCREEN_HANDLE_ITEM_ACTION;
    action: GridAction;
    id: string;
}

export function handleGridItemAction(
    action: GridAction,
    id: string
): GridScreenHandleItemAction {
    return {
        type: GRID_SCREEN_HANDLE_ITEM_ACTION,
        action,
        id
    };
}

export const GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK =
    'GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK';
export type GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK = typeof GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK;

export interface GridScreenHandleNavigationLinkClickAction {
    type: GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK;
    linkAction: GridAction;
    target?: LinkTarget;
}

export function handleNavigationLinkClick(
    linkAction: GridAction,
    target?: LinkTarget
): GridScreenHandleNavigationLinkClickAction {
    return {
        type: GRID_SCREEN_HANDLE_NAVIGATION_LINK_CLICK,
        linkAction,
        target
    };
}

export function getNoticeTypeForCopyMassAction(
    response: PerlRestApiResult
): {
    noticeType: NoticeType;
} {
    let noticeType = NoticeType.success;
    const { array_errors = [] } = response;

    if (array_errors.length !== 0) {
        noticeType = NoticeType.error;
    }

    return {
        noticeType
    };
}

export function getNoticeParamsForMassAction(
    response: PerlRestApiResult,
    selectionCount: number
): {
    successCount: number;
    skippedCount: number;
    noticeType: NoticeType;
} {
    let noticeType: NoticeType;
    const { updated_objects = [], skipped_objects = [], status } = response;

    const successCount = updated_objects.length;
    const skippedCount = skipped_objects.length;
    const successProportion = successCount / selectionCount;
    const isObjectsEmpty =
        !response.updated_objects && !response.skipped_objects;

    if (status && status === 'error') {
        noticeType = NoticeType.error;
    } else if (
        successProportion >= SUCCESS_PROPORTION_CHECKPOINT ||
        isObjectsEmpty
    ) {
        noticeType = NoticeType.success;
    } else if (successProportion <= ERROR_PROPORTION_CHECKPOINT) {
        noticeType = NoticeType.error;
    } else {
        noticeType = NoticeType.warning;
    }

    return {
        successCount,
        skippedCount,
        noticeType
    };
}

export function getNoticeParamsForMassActionMessage(
    response: PerlRestApiResult
): NoticeType {
    let noticeType: NoticeType;
    const { updated_objects = [], skipped_objects = [], status } = response;

    const successCount = updated_objects.length;
    const skippedCount = skipped_objects.length;
    const isObjectsEmpty =
        !response.updated_objects && !response.skipped_objects;

    if (status && status === 'error') {
        noticeType = NoticeType.error;
    } else if ((successCount > 0 && skippedCount === 0) || isObjectsEmpty) {
        noticeType = NoticeType.success;
    } else if (successCount === 0 && skippedCount > 0) {
        noticeType = NoticeType.error;
    } else {
        noticeType = NoticeType.warning;
    }

    return noticeType;
}

export function updateGridData(data: {
    gridData: GridData;
    clientInfo: ClientInfo;
    scrollPosition?: number;
    totalStats?: GridTotalStatsInfo;
    hasObjectsOverLimit: boolean;
    gridReqId: string;
    goalsSettings?: GoalDecl[];
    phraseContextData?: PhrasesFeaturesData;
}): UpdateGridData {
    return {
        type: UPDATE_GRID_DATA,
        ...data
    };
}

export function updateSuggestData(
    textSuggestItems: Array<string>
): UpdateSuggestData {
    return {
        type: UPDATE_SUGGEST_DATA,
        textSuggestItems
    };
}

export function resetSuggestData(): ResetSuggestData {
    return {
        type: RESET_SUGGEST_DATA
    };
}

export function updateFeatures(
    gridActionFeatures: GridActionFeatures
): UpdateFeatures {
    return {
        type: UPDATE_GRID_FEATURES,
        gridActionFeatures
    };
}

export function updateActionProgress(
    isActionInProgress: boolean
): UpdateActionProgress {
    return {
        type: UPDATE_ACTION_PROGRESS,
        isActionInProgress
    };
}

export function updateSelection(selection: GridSelection): UpdateGridSelection {
    return {
        type: UPDATE_GRID_SELECTION,
        selection
    };
}

export function editCampaign(
    campaignId: CampaignId,
    login: string
): EditCampaign {
    return {
        type: OPEN_EDIT_CAMPAIGN_POPUP,
        campaignId,
        login
    };
}

export const GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT =
    'GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT';
export type GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT = typeof GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT;

export interface GridScreenGoToCampaignsEditAction {
    type: GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT;
    login: string;
    campaignsIds: string[];
}

export function goToCampaignsEdit(
    login: string,
    campaignsIds: string[]
): GridScreenGoToCampaignsEditAction {
    return {
        type: GRID_SCREEN_GO_TO_CAMPAIGNS_EDIT,
        login,
        campaignsIds
    };
}

export function addEntity(
    data: GridEditedEntityAddData
): EditCampaign | EditGroups {
    switch (data.type) {
        case GridEditedEntityType.Campaign:
            return {
                type: OPEN_EDIT_CAMPAIGN_POPUP,
                campaignType: data.campaignType,
                login: data.login
            };
        case GridEditedEntityType.Group:
            return {
                type: OPEN_EDIT_GROUPS_POPUP,
                campaignId: data.campaignId,
                campaignType: data.campaignType,
                login: data.login
            };
        case GridEditedEntityType.Banner:
            return {
                type: OPEN_EDIT_GROUPS_POPUP,
                campaignId: data.campaignId,
                groupIds: [data.groupId],
                campaignType: data.campaignType,
                login: data.login,
                anchor: 'add-banner'
            };
        case GridEditedEntityType.Phrase:
            return {
                type: OPEN_EDIT_GROUPS_POPUP,
                campaignId: data.campaignId,
                groupIds: [data.groupId],
                campaignType: data.campaignType,
                login: data.login,
                anchor: 'add-phrase'
            };
        default:
            throw 'unsupported entity type';
    }
}

export const SWITCH_TO_OLD_INTERFACE = 'SWITCH_TO_OLD_INTERFACE';
export type SWITCH_TO_OLD_INTERFACE = typeof SWITCH_TO_OLD_INTERFACE;

export interface SwitchToOldInterface {
    type: SWITCH_TO_OLD_INTERFACE;
}

export function switchToOldInterface(): SwitchToOldInterface {
    return {
        type: SWITCH_TO_OLD_INTERFACE
    };
}

export function editGroups(
    groupIds: string[],
    campaignId: CampaignId,
    campaignType: CampaignType,
    login: string,
    bannerIds?: string[]
): EditGroups {
    return {
        type: OPEN_EDIT_GROUPS_POPUP,
        groupIds,
        campaignId,
        campaignType,
        login,
        bannerIds
    };
}

export function closeEditPopup(): CloseEditPopup {
    return {
        type: CLOSE_EDIT_POPUP
    };
}

export function scrollTableStart(): ScrollTableStart {
    return {
        type: SCROLL_TABLE_START
    };
}

export function scrollTableEnd(): ScrollTableEnd {
    return {
        type: SCROLL_TABLE_END
    };
}

export const GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE =
    'GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE';
export type GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE = typeof GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE;

export interface GridScreenHandleColumnsVisibilityChangeAction {
    type: GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE;
    columns: string[];
}

export function handleGridColumnsVisibilityChange(
    columns: string[]
): GridScreenHandleColumnsVisibilityChangeAction {
    return {
        type: GRID_SCREEN_HANDLE_COLUMNS_VISIBILITY_CHANGE,
        columns
    };
}

export const GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE =
    'GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE';
export type GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE = typeof GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE;

export interface GridScreenHandleColumnsOrderChangeAction {
    type: GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE;
    columnOrder: string[];
}

export function handleGridColumnsOrderChange(
    columnOrder: string[]
): GridScreenHandleColumnsOrderChangeAction {
    return {
        type: GRID_SCREEN_HANDLE_COLUMNS_ORDER_CHANGE,
        columnOrder
    };
}

export const GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE =
    'GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE';
export type GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE = typeof GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE;

export interface GridScreenHandleColumnsWidthUpdateAction {
    type: GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE;
    width: number;
    key?: GridViewType;
}

export function handleGridColumnsWidthUpdate(
    width: number,
    key?: GridViewType
): GridScreenHandleColumnsWidthUpdateAction {
    return {
        type: GRID_SCREEN_HANDLE_COLUMNS_WIDTH_UPDATE,
        width,
        key
    };
}

export const REQUEST_GRID_ROW = 'REQUEST_GRID_ROW';
export type REQUEST_GRID_ROW = typeof REQUEST_GRID_ROW;

export interface RequestGridRow {
    type: REQUEST_GRID_ROW;
    index: number;
}

export function requestGridRow(index: number): RequestGridRow {
    return {
        type: REQUEST_GRID_ROW,
        index
    };
}

export const RESET_GRID_DATA = 'RESET_GRID_DATA';
export type RESET_GRID_DATA = typeof RESET_GRID_DATA;

export interface ResetGridData {
    type: RESET_GRID_DATA;
}

export function resetGridData(): ResetGridData {
    return {
        type: RESET_GRID_DATA
    };
}

export const FETCH_GRID_GROUPS_COUNTS = 'FETCH_GRID_GROUPS_COUNTS';
export type FETCH_GRID_GROUPS_COUNTS = typeof FETCH_GRID_GROUPS_COUNTS;

export interface FetchGridGroupsCounts {
    type: FETCH_GRID_GROUPS_COUNTS;
}

export function fetchGridGroupsCounts(): FetchGridGroupsCounts {
    return {
        type: FETCH_GRID_GROUPS_COUNTS
    };
}

export const FETCH_SUGGEST_DATA = 'FETCH_SUGGEST_DATA';
export type FETCH_SUGGEST_DATA = typeof FETCH_SUGGEST_DATA;

export interface FetchSuggestData {
    type: FETCH_SUGGEST_DATA;
}

export function fetchSuggestData(): FetchSuggestData {
    return {
        type: FETCH_SUGGEST_DATA
    };
}

export function updateEditCellData(
    cellKeysFields: CellKeyFields,
    data: GridEditCellStatus
): UpdateEditCellData {
    return {
        type: UPDATE_CELL_EDIT_DATA,
        cellKeysFields,
        data
    };
}

export function deleteEditCellData(
    cellKeysFields: CellKeyFields
): DeteleEditCellData {
    return {
        type: DELETE_CELL_EDIT_DATA,
        cellKeysFields
    };
}

export const FETCH_TOOLTIP_DATA = 'FETCH_TOOLTIP_DATA';
export type FETCH_TOOLTIP_DATA = typeof FETCH_TOOLTIP_DATA;

export interface FetchTooltipData {
    type: FETCH_TOOLTIP_DATA;
    id: DocCenterTooltip;
}

export function fetchTooltipData(id: DocCenterTooltip): FetchTooltipData {
    return {
        type: FETCH_TOOLTIP_DATA,
        id
    };
}

export const UPDATE_TOOLTIP_DATA = 'UPDATE_TOOLTIP_DATA';
export type UPDATE_TOOLTIP_DATA = typeof UPDATE_TOOLTIP_DATA;

export interface UpdateTooltipData {
    type: UPDATE_TOOLTIP_DATA;
    id: string;
    value: string;
}

export function updateTooltipData(data: DocCenterData): UpdateTooltipData {
    return {
        type: UPDATE_TOOLTIP_DATA,
        id: data.id,
        value: data.value
    };
}

export const RENDER_ON_FINISHED = 'RENDER_ON_FINISHED';
export type RENDER_ON_FINISHED = typeof RENDER_ON_FINISHED;

export interface RenderOnFinished {
    type: RENDER_ON_FINISHED;
    reqId: string;
}

export function onRenderFinished(reqId: string): RenderOnFinished {
    return {
        type: RENDER_ON_FINISHED,
        reqId
    };
}

export interface UpdateAdditionalFilter {
    type: UPDATE_ADDITIONAL_FILTER;
    view: GridViewType;
    additionalFilter: ScreenAdditionalFilter;
}

export function updateAdditionalFilter(
    view: GridViewType,
    additionalFilter: ScreenAdditionalFilter
): UpdateAdditionalFilter {
    return {
        type: UPDATE_ADDITIONAL_FILTER,
        view,
        additionalFilter
    };
}

export interface UpdateCalcTypeFilter {
    type: UPDATE_CALC_TYPE_FILTER;
    calcTypeFilter: CampaignsCalcTypeFilter;
}

export function updateCalcTypeFilter(
    calcTypeFilter: CampaignsCalcTypeFilter
): UpdateCalcTypeFilter {
    return {
        type: UPDATE_CALC_TYPE_FILTER,
        calcTypeFilter
    };
}

export const SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM =
    'SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM';
export type SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM = typeof SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM;

export interface SubmitGridScreenRecommendationItem {
    type: SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM;
    id: string;
    recommendationType: RecommendationType;
    keys: Array<RecommendationKey>;
    itemName: string;
}

export function submitGridScreenRecommendationItem(
    id: string,
    recommendationType: RecommendationType,
    keys: Array<RecommendationKey>,
    itemName: string
): SubmitGridScreenRecommendationItem {
    return {
        type: SUBMIT_GRID_SCREEN_RECOMMENDATION_ITEM,
        id,
        recommendationType,
        keys,
        itemName
    };
}

export const GET_GRID_RECOMMENDATION_APPLY_STATUS =
    'GET_GRID_RECOMMENDATION_APPLY_STATUS';
export type GET_GRID_RECOMMENDATION_APPLY_STATUS = typeof GET_GRID_RECOMMENDATION_APPLY_STATUS;

export interface GetGridRecommendationApplyStatus {
    type: GET_GRID_RECOMMENDATION_APPLY_STATUS;
    recommendationType: RecommendationType;
    keys: Array<RecommendationKey>;
    itemName: string;
}

export function getGridRecommendationApplyStatus(
    recommendationType: RecommendationType,
    keys: Array<RecommendationKey>,
    itemName: string
): GetGridRecommendationApplyStatus {
    return {
        type: GET_GRID_RECOMMENDATION_APPLY_STATUS,
        recommendationType,
        keys,
        itemName
    };
}

export const UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM =
    'UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM';
export type UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM = typeof UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM;

export interface UpdateGridScreenRecommendationItem {
    type: UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM;
    id: string;
    recommendationType: RecommendationType;
    status: GdRecommendationStatus;
}

export function updateGridScreenRecommendationItem(
    id: string,
    recommendationType: RecommendationType,
    status: GdRecommendationStatus
): UpdateGridScreenRecommendationItem {
    return {
        type: UPDATE_GRID_SCREEN_RECOMMENDATION_ITEM,
        id,
        recommendationType,
        status
    };
}

export const OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP =
    'OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP';
export type OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP = typeof OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP;

export interface OpenGridScreenRecommendationsPopup {
    type: OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP;
    view: GridViewType;
}

export function openGridScreenRecommendationsPopup(
    view: GridViewType
): OpenGridScreenRecommendationsPopup {
    return {
        type: OPEN_GRID_SCREEN_RECOMMENDATIONS_POPUP,
        view
    };
}

export const FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT =
    'FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT';
export type FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT = typeof FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT;

export interface FireShowGridScreenRecommendationEvent {
    type: FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT;
    recommendationType: RecommendationType;
}

export function fireShowGridScreenRecommendationEvent(
    recommendationType: RecommendationType
): FireShowGridScreenRecommendationEvent {
    return {
        type: FIRE_SHOW_GRID_SCREEN_RECOMMENDATION_EVENT,
        recommendationType
    };
}

export const FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT =
    'FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT';
export type FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT = typeof FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT;

export interface FireGridScreenSendLicenseButtonClickMetrikaEvent {
    type: FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT;
}

export function fireGridScreenSendLicenseButtonClickMetrikaEvent(): FireGridScreenSendLicenseButtonClickMetrikaEvent {
    return {
        type: FIRE_GRID_SCREEN_SEND_LICENSE_BUTTON_CLICK_METRIKA_EVENT
    };
}

export const SET_GRID_DOCUMENT_TITLE = 'SET_GRID_DOCUMENT_TITLE';
export type SET_GRID_DOCUMENT_TITLE = typeof SET_GRID_DOCUMENT_TITLE;

export interface SetGridDocumentTitle {
    type: SET_GRID_DOCUMENT_TITLE;
}

export function setGridDocumentTitle(): SetGridDocumentTitle {
    return {
        type: SET_GRID_DOCUMENT_TITLE
    };
}
export interface SwitchGridTotalBoxVisible {
    type: SWITCH_GRID_TOTAL_BOX_VISIBLE;
}

export function switchGridTotalBoxVisible(): SwitchGridTotalBoxVisible {
    return {
        type: SWITCH_GRID_TOTAL_BOX_VISIBLE
    };
}

export const TOGGLE_COMPACT_VIEW = 'TOGGLE_COMPACT_VIEW';
export type TOGGLE_COMPACT_VIEW = typeof TOGGLE_COMPACT_VIEW;

export interface ToggleCompactView {
    type: TOGGLE_COMPACT_VIEW;
}

export function toggleCompactView(): ToggleCompactView {
    return {
        type: TOGGLE_COMPACT_VIEW
    };
}

export interface UpdateGridFilterDict {
    type: UPDATE_GRID_FILTER_DICT;
    filterDict?: FilterGridViewDict;
}

export function updateGridFilterDict(
    filterDict?: FilterGridViewDict
): UpdateGridFilterDict {
    return {
        type: UPDATE_GRID_FILTER_DICT,
        filterDict
    };
}

export interface UpdateGridFilterPeriod {
    type: UPDATE_GRID_FILTER_PERIOD;
    period: PeriodFilterValue;
}

export function updateGridFilterPeriod(
    period: PeriodFilterValue
): UpdateGridFilterPeriod {
    return {
        type: UPDATE_GRID_FILTER_PERIOD,
        period
    };
}

export const GET_BANNER_MODERATION_REJECT_REASONS =
    'GET_BANNER_MODERATION_REJECT_REASONS';
export type GET_BANNER_MODERATION_REJECT_REASONS = typeof GET_BANNER_MODERATION_REJECT_REASONS;

export interface GetBannnerModerationRejectReasons {
    type: GET_BANNER_MODERATION_REJECT_REASONS;
    bid: string;
}

export function getBannerModerationRejectReasons(
    bid: string
): GetBannnerModerationRejectReasons {
    return {
        type: GET_BANNER_MODERATION_REJECT_REASONS,
        bid
    };
}

export const UPDATE_BANNER_MODERATION_REJECT_REASONS =
    'UPDATE_BANNER_MODERATION_REJECT_REASONS';
export type UPDATE_BANNER_MODERATION_REJECT_REASONS = typeof UPDATE_BANNER_MODERATION_REJECT_REASONS;

export interface UpdateBannerModerationRejectReasons {
    type: UPDATE_BANNER_MODERATION_REJECT_REASONS;
    bid: string;
    reasons: RejectReasonsData;
}

export function updateBannerModerationRejectReasons(
    bid: string,
    reasons: RejectReasonsData
): UpdateBannerModerationRejectReasons {
    return {
        type: UPDATE_BANNER_MODERATION_REJECT_REASONS,
        bid,
        reasons
    };
}

export const SET_BANNER_MODERATION_REJECT_REASONS_ERROR =
    'SET_BANNER_MODERATION_REJECT_REASONS_ERROR';
export type SET_BANNER_MODERATION_REJECT_REASONS_ERROR = typeof SET_BANNER_MODERATION_REJECT_REASONS_ERROR;

export interface SetBannerModerationRejectReasonsError {
    type: SET_BANNER_MODERATION_REJECT_REASONS_ERROR;
    error: string;
    bid: string;
}

export function setBannerModerationRejectReasonsError(
    bid: string,
    error: string
): SetBannerModerationRejectReasonsError {
    return {
        type: SET_BANNER_MODERATION_REJECT_REASONS_ERROR,
        bid,
        error
    };
}

export const GET_GROUP_MODERATION_REJECT_REASONS =
    'GET_GROUP_MODERATION_REJECT_REASONS';
export type GET_GROUP_MODERATION_REJECT_REASONS = typeof GET_GROUP_MODERATION_REJECT_REASONS;

export interface GetGroupModerationRejectReasons {
    type: GET_GROUP_MODERATION_REJECT_REASONS;
    groupId: string;
}

export function getGroupModerationRejectReasons(
    groupId: string
): GetGroupModerationRejectReasons {
    return {
        type: GET_GROUP_MODERATION_REJECT_REASONS,
        groupId
    };
}

export const UPDATE_GROUP_MODERATION_REJECT_REASONS =
    'UPDATE_GROUP_MODERATION_REJECT_REASONS';
export type UPDATE_GROUP_MODERATION_REJECT_REASONS = typeof UPDATE_GROUP_MODERATION_REJECT_REASONS;

export interface UpdateGroupModerationRejectReasons {
    type: UPDATE_GROUP_MODERATION_REJECT_REASONS;
    groupId: string;
    reasons: RejectReasonsData;
}

export function updateGroupModerationRejectReasons(
    groupId: string,
    reasons: RejectReasonsData
): UpdateGroupModerationRejectReasons {
    return {
        type: UPDATE_GROUP_MODERATION_REJECT_REASONS,
        groupId,
        reasons
    };
}

export const SET_GROUP_MODERATION_REJECT_REASONS_ERROR =
    'SET_GROUP_MODERATION_REJECT_REASONS_ERROR';
export type SET_GROUP_MODERATION_REJECT_REASONS_ERROR = typeof SET_GROUP_MODERATION_REJECT_REASONS_ERROR;

export interface SetGroupModerationRejectReasonsError {
    type: SET_GROUP_MODERATION_REJECT_REASONS_ERROR;
    error: string;
    groupId: string;
}

export function setGroupModerationRejectReasonsError(
    groupId: string,
    error: string
): SetGroupModerationRejectReasonsError {
    return {
        type: SET_GROUP_MODERATION_REJECT_REASONS_ERROR,
        groupId,
        error
    };
}

export const SET_BANNER_AIMING_POPUP = 'SET_BANNER_AIMING_POPUP';
export type SET_BANNER_AIMING_POPUP = typeof SET_BANNER_AIMING_POPUP;

export interface SetBannerAimingPopupAction {
    type: SET_BANNER_AIMING_POPUP;
    payload?: BannerAimingPopupData;
}

export function setBannerAimingPopup(
    payload?: BannerAimingPopupData
): SetBannerAimingPopupAction {
    return {
        type: SET_BANNER_AIMING_POPUP,
        payload
    };
}

export const SET_BANNER_AIMING_POPUP_PENDING =
    'SET_BANNER_AIMING_POPUP_PENDING';
export type SET_BANNER_AIMING_POPUP_PENDING = typeof SET_BANNER_AIMING_POPUP_PENDING;

export interface SetBannerAimingPopupPendingAction {
    type: SET_BANNER_AIMING_POPUP_PENDING;
    payload: boolean;
}

export function setBannerAimingPopupPending(
    payload: boolean
): SetBannerAimingPopupPendingAction {
    return {
        type: SET_BANNER_AIMING_POPUP_PENDING,
        payload
    };
}

export const START_BANNER_AIMING = 'START_BANNER_AIMING';
export type START_BANNER_AIMING = typeof START_BANNER_AIMING;

export interface StartBannerAimingAction {
    type: START_BANNER_AIMING;
    login: string;
    bannerId: string;
    cookieTtl: number;
}

export function startBannerAiming(
    login: string,
    bannerId: string,
    cookieTtl: number
): StartBannerAimingAction {
    return {
        type: START_BANNER_AIMING,
        login,
        bannerId,
        cookieTtl
    };
}

export const ENTITY_ADD_SUCCESS = 'ENTITY_ADD_SUCCESS';
export type ENTITY_ADD_SUCCESS = typeof ENTITY_ADD_SUCCESS;

export interface EntityAddSuccess {
    type: ENTITY_ADD_SUCCESS;
    data: SaveSuccessData;
}

export function entityAddSuccess(data: SaveSuccessData): EntityAddSuccess {
    return {
        type: ENTITY_ADD_SUCCESS,
        data
    };
}

export const ENTITY_EDIT_SUCCESS = 'ENTITY_EDIT_SUCCESS';
export type ENTITY_EDIT_SUCCESS = typeof ENTITY_EDIT_SUCCESS;

export interface EntityEditSuccess {
    type: ENTITY_EDIT_SUCCESS;
}

export function entityEditSuccess(): EntityEditSuccess {
    return {
        type: ENTITY_EDIT_SUCCESS
    };
}

export const GRID_CHANGE_ADJUSTMENT_DATA = 'GRID_CHANGE_ADJUSTMENT_DATA';
export type GRID_CHANGE_ADJUSTMENT_DATA = typeof GRID_CHANGE_ADJUSTMENT_DATA;

export interface GridChangeAdjustmentData {
    type: GRID_CHANGE_ADJUSTMENT_DATA;
    data: PhraseAdjustmentDataChange;
}

export function gridChangeAdjustmentData(
    data: PhraseAdjustmentDataChange
): GridChangeAdjustmentData {
    return {
        type: GRID_CHANGE_ADJUSTMENT_DATA,
        data
    };
}

export const UPDATE_ADJUSTMENT_FORECAST = 'UPDATE_ADJUSTMENT_FORECAST';
export type UPDATE_ADJUSTMENT_FORECAST = typeof UPDATE_ADJUSTMENT_FORECAST;

export interface UpdateAdjustmentForecast {
    type: UPDATE_ADJUSTMENT_FORECAST;
    params: GridFetchAdjustmentInfoParams;
}

export function updateAdjustmentForecast(
    params: GridFetchAdjustmentInfoParams
): UpdateAdjustmentForecast {
    return {
        type: UPDATE_ADJUSTMENT_FORECAST,
        params
    };
}

export const GRID_FETCH_ADJUSMENT_INFO = 'GRID_FETCH_ADJUSMENT_INFO';
export type GRID_FETCH_ADJUSMENT_INFO = typeof GRID_FETCH_ADJUSMENT_INFO;

export interface GridFetchAdjustmentInfo {
    type: GRID_FETCH_ADJUSMENT_INFO;
    params: GridFetchAdjustmentInfoParams;
}

export interface GridFetchAdjustmentInfoParams {
    phrase: string;
    minusWords: string[];
    geo: number[];
}

export function gridFetchAdjustmentInfo(
    params: GridFetchAdjustmentInfoParams
): GridFetchAdjustmentInfo {
    return {
        type: GRID_FETCH_ADJUSMENT_INFO,
        params
    };
}

export const SWITCH_ADJUSTMENT_VISIBILITY = 'SWITCH_ADJUSTMENT_VISIBILITY';
export type SWITCH_ADJUSTMENT_VISIBILITY = typeof SWITCH_ADJUSTMENT_VISIBILITY;

export interface SwitchAdjustmentVisibility {
    type: SWITCH_ADJUSTMENT_VISIBILITY;
    visible: boolean;
}

export function switchAdjustmentVisibility(
    visible: boolean
): SwitchAdjustmentVisibility {
    return {
        type: SWITCH_ADJUSTMENT_VISIBILITY,
        visible
    };
}

export const RESET_GRID_STATE = 'RESET_GRID_STATE';
export type RESET_GRID_STATE = typeof RESET_GRID_STATE;

export interface ResetGridState {
    type: RESET_GRID_STATE;
}

export function resetGridState(): ResetGridState {
    return {
        type: RESET_GRID_STATE
    };
}

export const SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER =
    'SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER';
export type SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER = typeof SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER;

export interface SwitchOnlyFavoriteCampaignsFilter {
    type: SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER;
}

export function switchOnlyFavoriteCampaignsFilter(): SwitchOnlyFavoriteCampaignsFilter {
    return {
        type: SWICH_ONLY_FAVORITE_CAMPAIGNS_FILTER
    };
}

export const OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL =
    'OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL';
export type OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL = typeof OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL;

export interface OpenMinusPhraseSelectorGridModal {
    type: OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL;
    groupsIds: string[];
}

export function openMinusPhraseSelectorGridModal(
    groupsIds: string[]
): OpenMinusPhraseSelectorGridModal {
    return {
        type: OPEN_MINUS_PHRASE_SELECTOR_GRID_MODAL,
        groupsIds
    };
}

export const CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL =
    'CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL';
export type CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL = typeof CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL;

export interface CloseMinusPhraseSelectorGridModal {
    type: CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL;
}

export function closeMinusPhraseSelectorGridModal(): CloseMinusPhraseSelectorGridModal {
    return {
        type: CLOSE_MINUS_PHRASE_SELECTOR_GRID_MODAL
    };
}

export const BIND_MINUS_PHRASE_TO_GRID_GROUPS =
    'BIND_MINUS_PHRASE_TO_GRID_GROUPS';
export type BIND_MINUS_PHRASE_TO_GRID_GROUPS = typeof BIND_MINUS_PHRASE_TO_GRID_GROUPS;

export interface BindMinusPhraseToGridGroups {
    type: BIND_MINUS_PHRASE_TO_GRID_GROUPS;
}

export function bindMinusPhraseToGridGroups(): BindMinusPhraseToGridGroups {
    return {
        type: BIND_MINUS_PHRASE_TO_GRID_GROUPS
    };
}

export const UNBIND_MINUS_PHRASE_TO_GRID_GROUPS =
    'UNBIND_MINUS_PHRASE_TO_GRID_GROUPS';
export type UNBIND_MINUS_PHRASE_TO_GRID_GROUPS = typeof UNBIND_MINUS_PHRASE_TO_GRID_GROUPS;

export interface UnbindMinusPhraseToGridGroups {
    type: UNBIND_MINUS_PHRASE_TO_GRID_GROUPS;
}

export function unbindMinusPhraseToGridGroups(): UnbindMinusPhraseToGridGroups {
    return {
        type: UNBIND_MINUS_PHRASE_TO_GRID_GROUPS
    };
}

export const SELECT_MINUS_PHRASES_IN_GRID_MODAL =
    'SELECT_MINUS_PHRASES_IN_GRID_MODAL';
export type SELECT_MINUS_PHRASES_IN_GRID_MODAL = typeof SELECT_MINUS_PHRASES_IN_GRID_MODAL;

export interface SelectMinusPhrasesInGridModal {
    type: SELECT_MINUS_PHRASES_IN_GRID_MODAL;
    id: string;
}

export function selectMinusPhrasesInGridModal(
    id: string
): SelectMinusPhrasesInGridModal {
    return {
        type: SELECT_MINUS_PHRASES_IN_GRID_MODAL,
        id
    };
}

export const UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL =
    'UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL';
export type UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL = typeof UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL;

export interface UpdateMinusPhraseItemsGridInModal {
    type: UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL;
    items: MinusPhraseSelectorItemsData[];
}

export function updateMinusPhraseItemsGridInModal(
    items: MinusPhraseSelectorItemsData[]
): UpdateMinusPhraseItemsGridInModal {
    return {
        type: UPDATE_MINUS_PHRASE_ITEMS_GRID_IN_MODAL,
        items
    };
}

export const SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL =
    'SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL';
export type SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL = typeof SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL;

export interface ShowErrorMinusPhraseItemsGridInModal {
    type: SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL;
}

export function showErrorMinusPhraseItemsGridInModal(): ShowErrorMinusPhraseItemsGridInModal {
    return {
        type: SHOW_ERROR_MINUS_PHRASE_ITEMS_GRID_IN_MODAL
    };
}

export const SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE =
    'SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE';
export type SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE = typeof SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE;

export interface ShowMinusPhraseBindSuccessMessage {
    type: SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE;
    successCount: number;
    totalCount: number;
    selectedMinusPhrasesCount: number;
    groupsIds: string[];
    campaignsIds: string[];
}

export function showMinusPhraseBindSuccessMessage(
    successCount: number,
    totalCount: number,
    selectedMinusPhrasesCount: number,
    groupsIds: string[],
    campaignsIds: string[]
): ShowMinusPhraseBindSuccessMessage {
    return {
        type: SHOW_MINUS_PHRASE_BIND_SUCCESS_MESSAGE,
        successCount,
        totalCount,
        groupsIds,
        campaignsIds,
        selectedMinusPhrasesCount
    };
}

export const SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE =
    'SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE';
export type SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE = typeof SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE;

export interface ShowMinusPhraseUnbindSuccessMessage {
    type: SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE;
    successCount: number;
    totalCount: number;
    selectedMinusPhrasesCount: number;
    groupsIds: Array<GroupId>;
    campaignsIds: string[];
}

export function showMinusPhraseUnbindSuccessMessage(
    successCount: number,
    totalCount: number,
    selectedMinusPhrasesCount: number,
    groupsIds: Array<GroupId>,
    campaignsIds: string[]
): ShowMinusPhraseUnbindSuccessMessage {
    return {
        type: SHOW_MINUS_PHRASE_UNBIND_SUCCESS_MESSAGE,
        successCount,
        totalCount,
        groupsIds,
        campaignsIds,
        selectedMinusPhrasesCount
    };
}

export const CLOSE_ALERT_GRID_ERROR = 'CLOSE_ALERT_GRID_ERROR';
export type CLOSE_ALERT_GRID_ERROR = typeof CLOSE_ALERT_GRID_ERROR;

export interface CloseAlertGridError {
    type: CLOSE_ALERT_GRID_ERROR;
}

export function closeAlertGridError(): CloseAlertGridError {
    return {
        type: CLOSE_ALERT_GRID_ERROR
    };
}

export const UPDATE_COLUMNS_SETTINGS_GRID_STATE =
    'UPDATE_COLUMNS_SETTINGS_GRID_STATE';
export type UPDATE_COLUMNS_SETTINGS_GRID_STATE = typeof UPDATE_COLUMNS_SETTINGS_GRID_STATE;

export interface UpdateColumnsSettingsGridState {
    type: UPDATE_COLUMNS_SETTINGS_GRID_STATE;
    columnsSettings: ColumnsSettingsViewDict;
}

export const updateColumnsSettingsGridState = (
    columnsSettings: ColumnsSettingsViewDict
): UpdateColumnsSettingsGridState => {
    return {
        type: UPDATE_COLUMNS_SETTINGS_GRID_STATE,
        columnsSettings
    };
};

export const UPDATE_COMMON_SETTINGS_GRID_STATE =
    'UPDATE_COMMON_SETTINGS_GRID_STATE';
export type UPDATE_COMMON_SETTINGS_GRID_STATE = typeof UPDATE_COMMON_SETTINGS_GRID_STATE;

export interface UpdateCommonSettingsGridState {
    type: UPDATE_COMMON_SETTINGS_GRID_STATE;
    commonSettingsDiff: CommonSettingEditorDiff;
}

export const updateCommonSettingsGridState = (
    commonSettingsDiff: CommonSettingEditorDiff
): UpdateCommonSettingsGridState => {
    return {
        type: UPDATE_COMMON_SETTINGS_GRID_STATE,
        commonSettingsDiff
    };
};

export const UPDATE_GOALS_SETTINGS_GRID_STATE =
    'UPDATE_GOALS_SETTINGS_GRID_STATE';
export type UPDATE_GOALS_SETTINGS_GRID_STATE = typeof UPDATE_GOALS_SETTINGS_GRID_STATE;

export interface UpdateGoalsSettingsGridState {
    type: UPDATE_GOALS_SETTINGS_GRID_STATE;
    goalsSettings: GoalDecl[];
}

export const updateGoalsSettingsGridState = (
    goalsSettings: GoalDecl[]
): UpdateGoalsSettingsGridState => {
    return {
        type: UPDATE_GOALS_SETTINGS_GRID_STATE,
        goalsSettings
    };
};

export interface OnGridSourceParamsChanged {
    type: ON_GRID_SOURCE_PARAMS_CHANGED;
    params: GridSourceParams;
    query: Query;
    routeParams: Params;
}

export const onGridSourceParamsChanged = (
    params: GridSourceParams,
    query: Query,
    routeParams: Params
): OnGridSourceParamsChanged => {
    return {
        type: ON_GRID_SOURCE_PARAMS_CHANGED,
        params,
        query,
        routeParams
    };
};

export const OPEN_DUPLICATE_GROUPS_MODAL = 'OPEN_DUPLICATE_GROUPS_MODAL';
export type OPEN_DUPLICATE_GROUPS_MODAL = typeof OPEN_DUPLICATE_GROUPS_MODAL;

export interface OpenDuplicateGroupsModal {
    type: OPEN_DUPLICATE_GROUPS_MODAL;
    campaignsIds: string[];
    groupsIds: string[];
    groupsCacheKey: string;
    login: string;
}

export const openDuplicateGroupsModal = (
    campaignsIds: string[],
    groupsIds: string[],
    groupsCacheKey: string,
    login: string
): OpenDuplicateGroupsModal => {
    return {
        type: OPEN_DUPLICATE_GROUPS_MODAL,
        campaignsIds,
        groupsIds,
        groupsCacheKey,
        login
    };
};

export const UPDATE_DUPLICATE_GROUPS_ITEMS_DATA =
    'UPDATE_DUPLICATE_GROUPS_ITEMS_DATA';
export type UPDATE_DUPLICATE_GROUPS_ITEMS_DATA = typeof UPDATE_DUPLICATE_GROUPS_ITEMS_DATA;

export interface UpdateDuplicateGroupsItemsData {
    type: UPDATE_DUPLICATE_GROUPS_ITEMS_DATA;
    items: Array<DuplicateItem>;
    isModalOpen: boolean;
}

export const updateDuplicateGroupsItemsData = (
    items: Array<DuplicateItem>,
    isModalOpen: boolean
): UpdateDuplicateGroupsItemsData => {
    return {
        type: UPDATE_DUPLICATE_GROUPS_ITEMS_DATA,
        items,
        isModalOpen
    };
};

export const COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD =
    'COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD';
export type COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD = typeof COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD;

export interface CopyRetargetingRulesInfoToClipboard {
    type: COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD;
    text: string;
}

export const copyRetargetingRulesInfoToClipboard = (
    text: string
): CopyRetargetingRulesInfoToClipboard => {
    return {
        type: COPY_RETARGETING_RULES_INFO_TO_CLIPBOARD,
        text
    };
};

export const CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS =
    'CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS';
export type CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS = typeof CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS;

export interface ChangeCopiedRetargetingRulesTooltipStatus {
    type: CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS;
    newStatus: CopyTooltipStatus;
}

export const changeCopiedRetargetingRulesTooltipStatus = (
    newStatus: CopyTooltipStatus
): ChangeCopiedRetargetingRulesTooltipStatus => {
    return {
        type: CHANGE_COPIED_RETARGETING_RULES_TOOLTIP_STATUS,
        newStatus
    };
};

export type GridScreenAction =
    | OnGridSourceParamsChanged
    | UpdateGridData
    | UpdateGridSelection
    | UpdateSuggestData
    | UpdateFeatures
    | UpdateActionProgress
    | EditCampaign
    | UnbindMinusPhraseToGridGroups
    | BindMinusPhraseToGridGroups
    | OpenMinusPhraseSelectorGridModal
    | SelectMinusPhrasesInGridModal
    | CloseMinusPhraseSelectorGridModal
    | EditGroups
    | UpdateColumnsWidthData
    | ColumnsVisibilityChangeData
    | ColumnsOrderChangeData
    | UpdateScrollPosition
    | CloseEditPopup
    | RequestGridRow
    | ResetGridData
    | UpdateEditCellData
    | UpdateGridFilterDict
    | DeteleEditCellData
    | FetchGridGroupsCounts
    | FetchSuggestData
    | FetchTooltipData
    | ScrollTableStart
    | ScrollTableEnd
    | UpdateTooltipData
    | SetGridDocumentTitle
    | RenderOnFinished
    | FireShowGridScreenRecommendationEvent
    | UpdateGridScreenRecommendationItem
    | SubmitGridScreenRecommendationItem
    | GetGridRecommendationApplyStatus
    | OpenGridScreenRecommendationsPopup
    | FireGridScreenSendLicenseButtonClickMetrikaEvent
    | SwitchToOldInterface
    | UpdateAdditionalFilter
    | SwitchGridTotalBoxVisible
    | UpdateAdditionalFilter
    | GetBannnerModerationRejectReasons
    | UpdateBannerModerationRejectReasons
    | SetBannerModerationRejectReasonsError
    | GetGroupModerationRejectReasons
    | UpdateGroupModerationRejectReasons
    | SetGroupModerationRejectReasonsError
    | SetBannerAimingPopupAction
    | SetBannerAimingPopupPendingAction
    | StartBannerAimingAction
    | UpdateGridFilterPeriod
    | EntityAddSuccess
    | EntityEditSuccess
    | GridChangeAdjustmentData
    | GridFetchAdjustmentInfo
    | SwitchAdjustmentVisibility
    | ToggleCompactView
    | UpdateAdjustmentForecast
    | GridScreenCancelCellEditAction
    | GridScreenGoToStatisticsAction
    | GridScreenGoToCampaignModerationOfferAction
    | GridScreenGoToBannersEditAction
    | GoToInternalBannersEditAction
    | GoToInternalBannersEditForCreationAction
    | GridScreenGoToBannersMassEditAction
    | GridScreenGoToInternalBannersMassEditAction
    | GridScreenGoToGroupsEditAction
    | GoToGroupsEditForCreationAction
    | GoToBannersEditForCreationAction
    | GridScreenGoToLogsAction
    | GridScreenHandleItemAction
    | GridScreenHandleNavigationLinkClickAction
    | GridScreenHandleColumnsVisibilityChangeAction
    | GridScreenHandleColumnsOrderChangeAction
    | GridScreenHandleColumnsWidthUpdateAction
    | GridScreenHandleBulkItemsAction
    | UpdateMinusPhraseItemsGridInModal
    | ShowMinusPhraseBindSuccessMessage
    | ShowMinusPhraseUnbindSuccessMessage
    | ShowErrorMinusPhraseItemsGridInModal
    | ResetGridState
    | ResetSuggestData
    | SwitchOnlyFavoriteCampaignsFilter
    | GridScreenGoToDirectPaymentInstructions
    | CloseAlertGridError
    | UpdateColumnsSettingsGridState
    | UpdateCommonSettingsGridState
    | UpdateGoalsSettingsGridState
    | OpenDuplicateGroupsModal
    | UpdateDuplicateGroupsItemsData
    | GridScreenGoToCampaignsEditAction
    | CopyRetargetingRulesInfoToClipboard
    | ChangeCopiedRetargetingRulesTooltipStatus;
